import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { toast } from "sonner";

import closeIcon from "assets/svg/upload-modal-close.svg";
import dropGraphic from "assets/svg/upload-modal-dropzone-graphic.svg";
import {
  uploadStatusExcel,
  getStatusTable,
  updateRowStatus,
  previewStatusExcelMessages,
  type StatusTableRow,
  type ExcelImportPreviewBlock,
} from "api/statusExcelUpload";
import ConfirmMessageModal from "components/ConfirmMessageModal";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";

export type UploadStatusModalProps = {
  open: boolean;
  onClose: () => void;
  onUploadComplete?: () => void;
};

type Phase = "pick" | "uploading" | "browse";

const EXCEL_ACCEPT =
  ".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel";

const MARK_TYPES = ["Word", "Label", "Logo", "Sound", "Colour", "3D"];
const COL_LETTERS = ["A", "B", "C", "D", "E", "F", "G"];

const APPLICATION_STATUS_OPTIONS = [
  "TM No. Allotted",
  "Formalities Check Pass",
  "Formalities Check Fail",
  "Marked for Exam",
  "Accepted",
  "Objected",
  "Examination Report - Accepted after Objection",
  "Examination Report - Hearing",
  "Advertised",
  "Advertised in Journal - Opposed",
  "Opposition Hearing",
  "Advertised in Journal - Not Opposed",
  "Registered",
  "Abandoned",
  "Refused",
] as const;

function isExcelFile(f: File): boolean {
  const n = f.name.toLowerCase();
  return n.endsWith(".xlsx") || n.endsWith(".xls");
}

function statusColor(status: string): string {
  const s = status.toLowerCase();
  if (s === "registered") return "#1a7f37";
  if (s === "objected" || s.includes("objection") || s === "refused" || s === "abandoned") return "#d93025";
  return "#222222";
}

/** Rounded-rect dotted stroke border for the dropzone */
function DottedDropzoneFrame({
  width,
  height,
  stroke,
}: {
  width: number;
  height: number;
  stroke: string;
}): JSX.Element | null {
  if (width < 8 || height < 8) return null;
  const inset = 1.5;
  const w = width - inset * 2;
  const h = height - inset * 2;
  const rx = 14;
  return (
    <svg className="pointer-events-none absolute inset-0" width={width} height={height} aria-hidden>
      <rect
        x={inset}
        y={inset}
        width={w}
        height={h}
        rx={rx}
        ry={rx}
        fill="none"
        stroke={stroke}
        strokeWidth={3}
        strokeDasharray="0.01 4.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function UploadStatusModal({
  open,
  onClose,
  onUploadComplete,
}: UploadStatusModalProps): JSX.Element | null {
  const inputRef = useRef<HTMLInputElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  const dragDepthRef = useRef(0);
  const dirtyRef = useRef(false);

  const [phase, setPhase] = useState<Phase>("pick");
  const [files, setFiles] = useState<File[]>([]);
  /** Per-upload override for backend AUTO_STATUS_INFORM_ON_EXCEL; `server` = omit field (use env). */
  const [excelWhatsAppPolicy, setExcelWhatsAppPolicy] = useState<"server" | "yes" | "no">("server");
  const [isDragging, setIsDragging] = useState(false);
  const [dropzoneSize, setDropzoneSize] = useState({ w: 0, h: 0 });

  const [rows, setRows] = useState<StatusTableRow[]>([]);
  const [tableLoading, setTableLoading] = useState(false);
  const [updatingRows, setUpdatingRows] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [previewBlocks, setPreviewBlocks] = useState<ExcelImportPreviewBlock[]>([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewServerDefaultOn, setPreviewServerDefaultOn] = useState(false);

  const allStatuses = Array.from(new Set(rows.map((r) => r.status).filter(Boolean))).sort();

  const filteredRows = rows.filter((r) => {
    if (typeFilter && typeFilter !== "__all__" && r.typeOfMark !== typeFilter) return false;
    if (statusFilter && statusFilter !== "__all__" && r.status !== statusFilter) return false;
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      return r.mark.toLowerCase().includes(q) || r.tmNo.toLowerCase().includes(q);
    }
    return true;
  });

  const fetchTable = useCallback(async () => {
    setTableLoading(true);
    try {
      const data = await getStatusTable();
      setRows(data.rows);
    } catch {
      setRows([]);
    } finally {
      setTableLoading(false);
    }
  }, []);

  const resetAll = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setPhase("pick");
    setFiles([]);
    setIsDragging(false);
    dragDepthRef.current = 0;
    setSearch("");
    setTypeFilter("");
    setStatusFilter("");
    setExcelWhatsAppPolicy("server");
    setPreviewBlocks([]);
    setPreviewOpen(false);
    setPreviewLoading(false);
    setPreviewServerDefaultOn(false);
    dirtyRef.current = false;
  }, []);

  const handleCloseClick = useCallback(() => {
    const shouldNotify = dirtyRef.current;
    resetAll();
    onClose();
    if (shouldNotify) onUploadComplete?.();
  }, [onClose, onUploadComplete, resetAll]);

  useEffect(() => {
    if (open) resetAll();
  }, [open, resetAll]);

  useLayoutEffect(() => {
    const el = bodyRef.current;
    if (!open || !el || phase !== "pick") return;
    const measure = (): void => {
      const r = el.getBoundingClientRect();
      setDropzoneSize({ w: Math.round(r.width), h: Math.round(r.height) });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [open, phase]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") handleCloseClick();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, handleCloseClick]);

  const addFiles = useCallback((list: FileList | File[] | null) => {
    if (!list?.length) return;
    const excel = Array.from(list).filter(isExcelFile);
    if (excel.length === 0) {
      toast.error("Please choose an Excel file (.xlsx or .xls).");
      return;
    }
    setFiles([excel[0]]);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      dragDepthRef.current = 0;
      setIsDragging(false);
      addFiles(e.dataTransfer.files);
    },
    [addFiles],
  );

  const runUpload = useCallback(async (): Promise<void> => {
    const file = files[0];
    if (!file) return;
    const ac = new AbortController();
    abortRef.current = ac;
    setPhase("uploading");
    try {
      const autoInform =
        excelWhatsAppPolicy === "yes" ? true : excelWhatsAppPolicy === "no" ? false : undefined;
      await uploadStatusExcel(file, ac.signal, { autoInform });
      dirtyRef.current = true;
      toast.success("Status import finished.");
      await fetchTable();
      setPhase("browse");
    } catch (err) {
      if (err instanceof Error && err.name === "AbortError") return;
      const msg = err instanceof Error ? err.message : "Upload failed";
      toast.error(msg);
      setPhase("pick");
    } finally {
      abortRef.current = null;
    }
  }, [files, excelWhatsAppPolicy, fetchTable]);

  const handleConfirmUpload = async (): Promise<void> => {
    const file = files[0];
    if (!file || !isExcelFile(file)) {
      toast.error("Select an Excel file exported from the status table (same columns).");
      return;
    }
    // Show preview confirmation whenever WhatsApp messages MIGHT go out — that's both
    // "yes" (explicit send) and "server" (relies on AUTO_STATUS_INFORM_ON_EXCEL env).
    // Only "no" skips preview because admin has explicitly opted out of sending.
    if (excelWhatsAppPolicy !== "no") {
      setPreviewLoading(true);
      try {
        const preview = await previewStatusExcelMessages(file);
        setPreviewBlocks(preview.blocks);
        setPreviewServerDefaultOn(preview.autoStatusInformDefault);
        setPreviewOpen(true);
      } catch (err) {
        toast.error(err instanceof Error ? err.message : "Preview failed");
      } finally {
        setPreviewLoading(false);
      }
      return;
    }
    await runUpload();
  };

  const handleStatusChange = useCallback(async (row: StatusTableRow, newStatus: string): Promise<void> => {
    if (newStatus === row.status) return;
    setUpdatingRows((prev) => new Set(prev).add(row.id));
    try {
      await updateRowStatus(row.leadId, row.tmNo, newStatus);
      setRows((prev) =>
        prev.map((r) => (r.id === row.id ? { ...r, status: newStatus } : r)),
      );
      dirtyRef.current = true;
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to update status");
    } finally {
      setUpdatingRows((prev) => {
        const next = new Set(prev);
        next.delete(row.id);
        return next;
      });
    }
  }, []);

  const handleUploadAnother = useCallback(() => {
    setPhase("pick");
    setFiles([]);
  }, []);

  // Whether messages will actually be sent on confirm = explicit "yes" OR ("server" AND server default is on).
  const willActuallySend =
    excelWhatsAppPolicy === "yes" ||
    (excelWhatsAppPolicy === "server" && previewServerDefaultOn);

  const previewIntroLine = willActuallySend
    ? excelWhatsAppPolicy === "yes"
      ? "_The following WhatsApp messages will be sent on confirm:_"
      : "_Server default has WhatsApp auto-send ON — the following messages will be sent on confirm:_"
    : "_Server default has WhatsApp auto-send OFF — preview only. The status import will apply, but no WhatsApp messages will be sent._";

  const previewBlockTexts =
    previewBlocks.length > 0
      ? [
          previewIntroLine,
          ...previewBlocks.map((b) => {
            const recipient = b.clientName?.trim() || `Lead ${b.leadId.slice(0, 8)}`;
            const subline =
              b.kind === "noChange"
                ? `Monthly update — no status change · ${b.tmNos.length} TM No.`
                : `New status: ${b.status ?? ""} · ${b.tmNos.length} TM No.`;
            return `*To: ${recipient}*\n_${subline}_\n\n${b.message || "(empty template)"}`;
          }),
        ]
      : [
          willActuallySend
            ? "*No WhatsApp messages will be sent for this upload.*\n\nEither nothing in the Excel changes existing statuses, or every matching client was already sent a monthly update this month.\n\nThe status import will still apply when you proceed."
            : "*No WhatsApp messages would be sent even if auto-send were enabled.*\n\nNothing in the Excel changes existing statuses, or every matching client was already notified this month.\n\nThe status import will still apply when you proceed.",
        ];

  const previewSummary =
    previewBlocks.length > 0
      ? (() => {
          const recipients = new Set(previewBlocks.map((b) => b.leadId));
          const verb = willActuallySend ? "will send" : "would send";
          return `Confirm status import (${verb} ${previewBlocks.length} WhatsApp message${
            previewBlocks.length === 1 ? "" : "s"
          } to ${recipients.size} client${recipients.size === 1 ? "" : "s"})`;
        })()
      : "Confirm status import";

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto pt-[60px] pb-10"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <div className="fixed inset-0 bg-[#222] opacity-60" aria-hidden onClick={handleCloseClick} />
      <div
        role="dialog"
        aria-modal
        aria-labelledby="upload-status-modal-title"
        className="relative z-10 flex w-[1107px] max-w-[calc(100vw-48px)] flex-col overflow-hidden rounded-[10px] bg-white shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex min-h-[60px] shrink-0 items-center justify-between border-b border-solid border-[#e3e3e3] px-5 py-0">
          <h2 id="upload-status-modal-title" className="text-base font-semibold leading-5 text-[#222222]">
            Update Status
          </h2>
          <button
            type="button"
            onClick={handleCloseClick}
            className="flex size-5 shrink-0 items-center justify-center rounded text-[#222] hover:bg-[#f5f5f5]"
            aria-label="Close"
          >
            <img src={closeIcon} alt="" width={20} height={20} className="block" />
          </button>
        </div>

        {/* Body */}
        <div className="flex flex-col p-5 min-h-0">
          {/* ── PICK phase: dropzone ── */}
          {phase === "pick" && (
            <div
              ref={bodyRef}
              className={`relative flex w-full min-h-[280px] md:min-h-[523px] flex-col items-center justify-center rounded-2xl bg-white overflow-hidden ${isDragging ? "bg-[#fafafa]" : ""}`}
              onDragEnter={(e) => { e.preventDefault(); dragDepthRef.current += 1; setIsDragging(true); }}
              onDragLeave={(e) => { e.preventDefault(); dragDepthRef.current = Math.max(0, dragDepthRef.current - 1); if (dragDepthRef.current === 0) setIsDragging(false); }}
              onDragOver={(e) => e.preventDefault()}
              onDrop={handleDrop}
            >
              <DottedDropzoneFrame
                width={dropzoneSize.w}
                height={dropzoneSize.h}
                stroke={isDragging ? "#222222" : "#d8daeb"}
              />
              <div className="relative z-[1] flex w-full max-w-[480px] flex-col items-center gap-5 px-5 py-10">
                <img src={dropGraphic} alt="" width={60} height={60} className="size-[60px] shrink-0" />
                <div className="flex w-full flex-col items-center gap-2 text-center">
                  <p className="w-full text-xl font-semibold leading-7 text-[#222222]">
                    Drag and drop your files
                  </p>
                  <p className="w-full text-xs font-medium leading-4 text-[#90a1b9]">
                    Excel (.xlsx) with columns: No., Mark, Type, Class, TM No., Appl Date, Status.
                    TM No. is matched; Status is updated.
                  </p>
                </div>
                <input
                  ref={inputRef}
                  type="file"
                  className="sr-only"
                  accept={EXCEL_ACCEPT}
                  onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }}
                />
                <button
                  type="button"
                  onClick={() => inputRef.current?.click()}
                  className="flex h-9 w-fit min-w-[107px] shrink-0 items-center justify-center rounded-[10px] border border-solid border-[#eeeeee] bg-white px-4 text-xs font-normal leading-5 text-[#222222] hover:bg-[#fafafa]"
                >
                  Select file
                </button>
                {files.length > 0 && (
                  <p className="text-xs font-normal leading-5 text-[#222222]">
                    Selected: <span className="font-medium">{files[0].name}</span>
                  </p>
                )}
              </div>
            </div>
          )}

          {/* ── UPLOADING phase: spinner ── */}
          {phase === "uploading" && (
            <div className="flex min-h-[320px] items-center justify-center border border-solid border-[#eeeeee] rounded-2xl bg-[#f9f9f9] py-16">
              <div className="flex max-w-md flex-col items-center gap-6 text-center">
                <div
                  className="size-14 shrink-0 animate-spin rounded-full border-[3px] border-solid border-[#eeeeee] border-t-[#222222]"
                  aria-hidden
                />
                <div className="flex flex-col gap-2">
                  <p className="text-xl font-semibold leading-7 text-[#222222]">Processing your file</p>
                  <p className="text-xs font-medium leading-4 text-[#90a1b9]">
                    Updating application statuses from your spreadsheet…
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* ── BROWSE phase: spreadsheet table ── */}
          {phase === "browse" && (
            <>
              {/* Search + Filters row */}
              <div className="flex items-center gap-3 mb-3">
                <div className="flex flex-1 items-center gap-2 rounded-md border border-[#eeeeee] bg-[#F3F3F5] px-3 h-9">
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
                    <circle cx="6" cy="6" r="4.5" stroke="#90a1b9" strokeWidth="1.5" />
                    <path d="M9.5 9.5L12 12" stroke="#90a1b9" strokeWidth="1.5" strokeLinecap="round" />
                  </svg>
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search by mark or TM number..."
                    className="flex-1 bg-transparent text-xs font-normal leading-5 text-[#222222] placeholder:text-[#90a1b9] outline-none [background-color:transparent]"
                    style={{ background: "transparent" }}
                  />
                </div>
                <Select value={typeFilter || "__all__"} onValueChange={(v) => setTypeFilter(v === "__all__" ? "" : v)}>
                  <SelectTrigger className="w-[130px] h-9 text-xs bg-[#F3F3F5]">
                    <SelectValue placeholder="All Types" />
                  </SelectTrigger>
                  <SelectContent className="z-[200]">
                    <SelectItem value="__all__">All Types</SelectItem>
                    {MARK_TYPES.map((t) => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={statusFilter || "__all__"} onValueChange={(v) => setStatusFilter(v === "__all__" ? "" : v)}>
                  <SelectTrigger className="w-[150px] h-9 text-xs bg-[#F3F3F5]">
                    <SelectValue placeholder="All Status" />
                  </SelectTrigger>
                  <SelectContent className="z-[200]">
                    <SelectItem value="__all__">All Status</SelectItem>
                    {allStatuses.map((s) => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Spreadsheet table */}
              <div className="overflow-auto max-h-[min(60vh,500px)] rounded border border-[#d8d8d8]">
                <table
                  className="border-collapse text-left"
                  style={{ width: "100%", minWidth: 700, tableLayout: "fixed" }}
                >
                  <colgroup>
                    <col style={{ width: 52 }} />
                    <col style={{ width: "28%" }} />
                    <col style={{ width: "10%" }} />
                    <col style={{ width: "8%" }} />
                    <col style={{ width: "12%" }} />
                    <col style={{ width: "12%" }} />
                    <col />
                  </colgroup>
                  <thead>
                    {/* Column letter row */}
                    <tr>
                      {COL_LETTERS.map((letter) => (
                        <th
                          key={letter}
                          className="border border-[#d0d0d0] bg-white py-[5px] text-center text-[11px] font-normal leading-4 text-[#555555]"
                        >
                          {letter}
                        </th>
                      ))}
                    </tr>
                    {/* Column name row */}
                    <tr style={{ backgroundColor: "#4472C4" }}>
                      {["No", "Mark", "Type", "Class", "TM No", "Appl Date", "Status"].map((h) => (
                        <th
                          key={h}
                          className="border border-[#5580CF] px-3 py-2 text-xs font-semibold leading-5 text-white"
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {tableLoading ? (
                      <tr>
                        <td colSpan={7} className="border border-[#d8d8d8] px-3 py-8 text-center text-xs text-[#90a1b9]">
                          Loading…
                        </td>
                      </tr>
                    ) : filteredRows.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="border border-[#d8d8d8] px-3 py-8 text-center text-xs text-[#90a1b9]">
                          No applications found.
                        </td>
                      </tr>
                    ) : (
                      filteredRows.map((row) => {
                        const isSaving = updatingRows.has(row.id);
                        return (
                          <tr key={row.id} className="bg-white hover:bg-[#fafafa]">
                            <td className="border border-[#d8d8d8] px-3 py-2 text-xs font-normal leading-5 text-[#222222]">{row.no}</td>
                            <td className="border border-[#d8d8d8] px-3 py-2 text-xs font-normal leading-5 text-[#222222]">{row.mark}</td>
                            <td className="border border-[#d8d8d8] px-3 py-2 text-xs font-normal leading-5 text-[#222222]">{row.typeOfMark}</td>
                            <td className="border border-[#d8d8d8] px-3 py-2 text-xs font-normal leading-5 text-[#222222]">{row.classNumber}</td>
                            <td className="border border-[#d8d8d8] px-3 py-2 text-xs font-normal leading-5 text-[#222222]">{row.tmNo}</td>
                            <td className="border border-[#d8d8d8] px-3 py-2 text-xs font-normal leading-5 text-[#222222]">{row.applDate}</td>
                            <td className="border border-[#d8d8d8] p-0 text-xs">
                              <Select
                                value={row.status}
                                onValueChange={(v) => void handleStatusChange(row, v)}
                                disabled={isSaving}
                              >
                                <SelectTrigger
                                  className="h-full w-full rounded-none border-0 bg-transparent px-3 py-2 shadow-none focus:ring-0 focus:ring-offset-0 hover:bg-[#f0f4ff] disabled:opacity-60"
                                  style={{ color: statusColor(row.status), fontWeight: 600, fontSize: "0.75rem", lineHeight: "1.25rem" }}
                                >
                                  <SelectValue />
                                  {isSaving && (
                                    <span className="ml-1 inline-block size-3 shrink-0 animate-spin rounded-full border border-[#90a1b9] border-t-[#222]" />
                                  )}
                                </SelectTrigger>
                                <SelectContent className="z-[200]">
                                  {APPLICATION_STATUS_OPTIONS.map((s) => (
                                    <SelectItem key={s} value={s}>
                                      <span style={{ color: statusColor(s), fontWeight: s === row.status ? 600 : 400 }}>
                                        {s}
                                      </span>
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </td>
                          </tr>
                        );
                      })
                    )}
                    {/* Filler empty rows for spreadsheet look */}
                    {!tableLoading && filteredRows.length > 0 && filteredRows.length < 4 &&
                      Array.from({ length: 4 - filteredRows.length }).map((_, i) => (
                        <tr key={`empty-${i}`}>
                          {Array.from({ length: 7 }).map((__, j) => (
                            <td key={j} className="border border-[#d8d8d8] px-3 py-2 text-xs">&nbsp;</td>
                          ))}
                        </tr>
                      ))
                    }
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex w-full shrink-0 flex-row flex-wrap items-center justify-between gap-3 border-t border-solid border-[#eeeeee] px-3 py-3">
          {phase === "pick" && (
            <>
              <div className="flex min-w-0 flex-1 flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={handleCloseClick}
                  className="flex h-9 min-w-[66px] items-center justify-center rounded-[5.5px] border border-solid border-[#eeeeee] bg-white px-4 text-xs font-semibold leading-5 text-[#6d6d6d] hover:bg-[#fafafa]"
                >
                  Close
                </button>
                <div className="flex min-w-0 max-w-full flex-wrap items-center gap-2">
                  <label htmlFor="excel-wa-policy" className="shrink-0 text-xs font-medium text-[#555555]">
                    WhatsApp after import
                  </label>
                  <select
                    id="excel-wa-policy"
                    value={excelWhatsAppPolicy}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === "server" || v === "yes" || v === "no") {
                        setExcelWhatsAppPolicy(v);
                      }
                    }}
                    className="h-9 min-w-[180px] max-w-full rounded-md border border-solid border-[#eeeeee] bg-white px-2 text-xs text-[#222222]"
                  >
                    <option value="server">Use server default</option>
                    <option value="yes">Send for this upload</option>
                    <option value="no">Do not send</option>
                  </select>
                </div>
              </div>
              <button
                type="button"
                disabled={files.length === 0 || previewLoading}
                onClick={() => void handleConfirmUpload()}
                className="flex h-9 min-w-[141px] items-center justify-center rounded-md bg-[#222222] px-4 text-xs font-semibold leading-5 text-white hover:bg-[#333333] disabled:cursor-not-allowed disabled:opacity-50"
              >
                {previewLoading
                  ? "Preparing preview…"
                  : excelWhatsAppPolicy !== "no"
                    ? "Preview & Proceed"
                    : "Confirm & Proceed"}
              </button>
            </>
          )}
          {phase === "uploading" && (
            <button
              type="button"
              onClick={handleCloseClick}
              className="flex h-9 min-w-[66px] items-center justify-center rounded-[5.5px] border border-solid border-[#eeeeee] bg-white px-4 text-xs font-semibold leading-5 text-[#6d6d6d] hover:bg-[#fafafa]"
            >
              Cancel
            </button>
          )}
          {phase === "browse" && (
            <>
              <button
                type="button"
                onClick={handleUploadAnother}
                className="flex h-9 min-w-[140px] items-center justify-center rounded-[5.5px] border border-solid border-[#eeeeee] bg-white px-4 text-xs font-semibold leading-5 text-[#222222] hover:bg-[#fafafa]"
              >
                Upload another file
              </button>
              <button
                type="button"
                onClick={handleCloseClick}
                className="flex h-9 min-w-[66px] items-center justify-center rounded-md bg-[#222222] px-4 text-xs font-semibold leading-5 text-white hover:bg-[#333333]"
              >
                Close
              </button>
            </>
          )}
        </div>
      </div>
      <ConfirmMessageModal
        open={previewOpen}
        title={previewSummary}
        previewContent={previewBlockTexts}
        onClose={() => setPreviewOpen(false)}
        onConfirm={() => {
          setPreviewOpen(false);
          void runUpload();
        }}
      />
    </div>
  );
}
